import { z } from 'zod';
import { accessGate, conditionGroup, configKey, editableText, entityId, publishable, slug } from './primitives.js';

/**
 * REGRAS OPERACIONAIS PARAMETRIZAVEIS
 *
 * Cobre a regra dos 90 dias (Cap. 14), expiracao e renovacao (Cap. 15),
 * roteamento de contato (Cap. 19), moderacao (Cap. 23) e notificacoes.
 * Todos os prazos sao editaveis — o documento diz explicitamente
 * "o prazo devera ser editavel".
 */

/** Ciclo de vida do anuncio. */
export const listingLifecycleRules = z.object({
  /** Exige aprovacao de moderador antes de publicar (Cap. 4.2). */
  requiresModeration: z.boolean().default(true),
  /** Aprovacao automatica para planos confiaveis. */
  autoApproveForPlans: z.array(slug).default([]),
  /** Avisos antes do vencimento, em dias. */
  expiryWarningDays: z.array(z.number().int().min(0)).default([7, 1]),
  /** Quantos dias o anuncio expirado fica visivel no painel do anunciante. */
  retentionAfterExpiryDays: z.number().int().min(0).default(180),
  /** Pergunta periodica "O imovel ainda esta disponivel?" (Cap. 15.3). */
  availabilityCheck: z
    .object({
      enabled: z.boolean().default(true),
      everyDays: z.number().int().min(1).default(30),
      /** Pausa o anuncio se ninguem responder em N dias. */
      autoPauseAfterSilenceDays: z.number().int().min(0).default(0),
    })
    .default({}),
});

export type ListingLifecycleRules = z.infer<typeof listingLifecycleRules>;

/**
 * Regra dos 90 dias (Cap. 14). O gatilho e um dado, nao um `if (dias > 90)`.
 * Podem coexistir varias janelas: 60 dias gera um toque leve, 90 gera a
 * oferta de fee mensal.
 */
export const agingOpportunityRule = z.object({
  id: entityId,
  key: configKey,
  name: editableText,
  /** Dias de anuncio ativo sem venda que disparam a oportunidade. */
  thresholdDays: z.number().int().min(1).default(90),
  /** Restringe a certos planos/perfis/cidades. */
  gate: accessGate.default({ roles: [], plans: [] }),
  filter: conditionGroup.nullable().default(null),
  /** O que acontece ao atingir o prazo. */
  actions: z
    .array(
      z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('notify_backoffice'), queue: configKey }),
        z.object({ kind: z.literal('create_crm_task'), pipelineKey: configKey, stageKey: configKey }),
        z.object({ kind: z.literal('notify_advertiser'), templateKey: configKey }),
        z.object({ kind: z.literal('tag_listing'), tag: z.string() }),
        z.object({ kind: z.literal('offer_product'), productSlug: slug }),
      ]),
    )
    .default([]),
  /** Nao repete a oportunidade antes deste intervalo. */
  cooldownDays: z.number().int().min(0).default(30),
  publication: publishable.default({}),
});

export type AgingOpportunityRule = z.infer<typeof agingOpportunityRule>;

/** Roteamento de contato (Cap. 19.1). */
export const contactRoutingRules = z.object({
  /** Modo padrao quando nada mais se aplica. */
  defaultMode: z.enum(['direct', 'centralized']).default('centralized'),
  /** Sobrescritas em ordem: a primeira que casar vence. */
  overrides: z
    .array(
      z.object({
        when: conditionGroup,
        mode: z.enum(['direct', 'centralized']),
      }),
    )
    .default([]),
  /** Exige cadastro do visitante antes de revelar o contato. */
  requiresRegistration: z.boolean().default(false),
  /** Mascara o telefone do anunciante mesmo no modo direto. */
  maskAdvertiserPhone: z.boolean().default(true),
  /** Template da mensagem de WhatsApp; aceita {{codigo}}, {{titulo}}, {{url}}. */
  whatsappTemplate: editableText.default('Ola! Tenho interesse no imovel {{codigo}} - {{titulo}}. {{url}}'),
});

export type ContactRoutingRules = z.infer<typeof contactRoutingRules>;

/** Moderacao e antifraude (Cap. 23). */
export const moderationRules = z.object({
  duplicateDetection: z
    .object({
      enabled: z.boolean().default(true),
      /** Similaridade minima (0-1) para marcar como possivel duplicata. */
      titleSimilarity: z.number().min(0).max(1).default(0.85),
      /** Distancia em metros para considerar o mesmo imovel. */
      geoRadiusMeters: z.number().int().min(0).default(30),
      comparePhotoHashes: z.boolean().default(true),
    })
    .default({}),
  /** Preco fora de N desvios da mediana da cidade entra para revisao. */
  priceOutlierStdDev: z.number().min(0).default(3),
  blockExternalLinksInDescription: z.boolean().default(true),
  bannedTerms: z.array(z.string()).default([]),
  /** Motivos de denuncia oferecidos ao publico (Cap. 23.2). */
  reportReasons: z
    .array(z.object({ key: configKey, label: editableText, requiresDescription: z.boolean().default(false) }))
    .default([]),
  /** Denuncias acumuladas que suspendem o anuncio automaticamente. */
  autoSuspendAfterReports: z.number().int().min(0).default(0),
});

export type ModerationRules = z.infer<typeof moderationRules>;

/** Notificacoes: quais eventos avisam quem, por qual canal. */
export const notificationRule = z.object({
  key: configKey,
  event: z.string().min(1),
  channels: z.array(z.enum(['email', 'whatsapp', 'push', 'in_app', 'webhook'])).default(['email']),
  audience: z.enum(['advertiser', 'buyer', 'backoffice', 'admin']),
  templateKey: configKey,
  /** Agrupa varios eventos num unico envio dentro da janela. */
  throttleMinutes: z.number().int().min(0).default(0),
  publication: publishable.default({}),
});

export type NotificationRule = z.infer<typeof notificationRule>;

/**
 * Modelo de mensagem transacional.
 *
 * Confirmacao de conta, recuperacao de senha, aviso de novo lead — todos sao
 * dados editaveis no painel (Cap. 2.1, itens "textos" e "notificacoes"). O
 * corpo aceita variaveis no formato {{nome}}; a lista de variaveis validas de
 * cada modelo fica em `variables`, para o painel conseguir avisar quando o
 * administrador escrever uma que nao existe.
 */
export const messageTemplate = z.object({
  key: configKey,
  name: editableText,
  channel: z.enum(['email', 'whatsapp', 'push', 'in_app']).default('email'),
  subject: editableText,
  /** Texto simples. O HTML e montado pelo servico a partir do layout padrao. */
  body: editableText,
  /** Rotulo e destino do botao principal, quando houver. */
  actionLabel: editableText.nullable().default(null),
  actionPath: z.string().nullable().default(null),
  variables: z.array(z.string()).default([]),
  /** Minutos de validade do link, quando o modelo carrega um token. */
  linkTtlMinutes: z.number().int().min(1).nullable().default(null),
  publication: publishable.default({}),
});

export type MessageTemplate = z.infer<typeof messageTemplate>;

export const rulesConfig = z.object({
  version: z.string(),
  lifecycle: listingLifecycleRules.default({}),
  agingOpportunities: z.array(agingOpportunityRule).default([]),
  contactRouting: contactRoutingRules.default({}),
  moderation: moderationRules.default({}),
  notifications: z.array(notificationRule).default([]),
  /** Modelos das mensagens transacionais, indexados por chave. */
  templates: z.array(messageTemplate).default([]),
  /**
   * Politica de conta e senha. Sao regras de negocio, entao vivem na
   * configuracao — mudar o tamanho minimo da senha nao pode exigir deploy.
   */
  account: z
    .object({
      /** Exige confirmar o e-mail antes de publicar anuncio. */
      requiresEmailVerification: z.boolean().default(true),
      passwordMinLength: z.number().int().min(6).max(64).default(8),
      /** Exige ao menos uma maiuscula, uma minuscula e um numero. */
      passwordRequiresMixedCase: z.boolean().default(true),
      passwordRequiresNumber: z.boolean().default(true),
      /** Validade do link de recuperacao, em minutos. */
      passwordResetTtlMinutes: z.number().int().min(5).default(60),
      emailVerificationTtlMinutes: z.number().int().min(5).default(1440),
      /** Tentativas de login antes de exigir espera. */
      maxLoginAttempts: z.number().int().min(3).default(5),
      lockoutMinutes: z.number().int().min(1).default(15),
      /** Perfis de anunciante oferecidos no cadastro (Cap. 3.3). */
      advertiserKindsTaxonomy: configKey.default('tipo_anunciante'),
    })
    .default({}),
  /** Privacidade de localizacao permitida ao anunciante (Cap. 7.2). */
  locationPrivacyOptions: z
    .array(z.enum(['exact', 'neighborhood', 'region', 'city', 'hidden']))
    .default(['exact', 'neighborhood', 'region', 'city', 'hidden']),
  defaultLocationPrivacy: z.enum(['exact', 'neighborhood', 'region', 'city', 'hidden']).default('neighborhood'),
});

export type RulesConfig = z.infer<typeof rulesConfig>;
